import "./styles.scss";

import { Card } from "@amzn/stencil-react-components/card";
import { useResizeObserver } from "@amzn/stencil-react-components/hooks";
import { Col, Hr, Row, View } from "@amzn/stencil-react-components/layout";
import { Link, LinkTextDecorationOptions } from "@amzn/stencil-react-components/link";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import { Text } from "@amzn/stencil-react-components/text";
import {
  CFMModuleName,
  CustomerFeedback,
  CustomerFeedbackAlignment,
  CustomerFeedbackIconPosition,
  CustomerFeedbackMargins,
} from "common/components";
import { HardwareUpgradeInfoCard } from "common/components/HardwareUpgradeInfoCard";
import { useMessageBannersBox } from "common/components/MessageBannersBox/hook";
import { NoResultsFound } from "common/components/NoResultsFound";
import { PageMainContainer } from "common/components/PageMainContainer/PageMainContainer";
import { getString } from "common/uistringlabels/uiStringUtils";
import { PERMISSIONS } from "config";
import { useGetItems } from "hooks/catalogical-amznham";
import { UserPermissions } from "lib/services";
import HardwareFacetedFilter from "modules/components/HardwareFacetedFilter";
import SearchItemsList from "modules/components/SearchItemsList";
import React, { FC, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

export const PAGE_LIMIT = 500;
const COLUMN_WIDTH = 297;
const CFM_STYLE = "cfm-scroll";

const SearchResultsHardwarePage: FC = () => {
  const { ref } = useResizeObserver<HTMLDivElement>();
  const history = useHistory();

  const { addErrorBanner } = useMessageBannersBox();
  const { data: items, paginationInfo, error, isLoading, doGetItems } = useGetItems();

  const [filters, setFilters] = useState<{ [key: string]: string }>({ parentTaxonomyId: "-" });
  const addFacetFilter = (filter: { field: string; value: string }) => {
    const newFilters = { ...filters };
    newFilters[filter.field] = filter.value;
    setFilters(newFilters);
  };

  // Check permissions on component mount
  useEffect(() => {
    if (!UserPermissions.current.hasAuthorized) {
      // User hasn't been authorized yet, wait for authorization
      return;
    }

    if (!UserPermissions.current.hasPermission(PERMISSIONS.USE_HARDWARE_SHOP)) {
      // Only show error if we have valid permissions but user doesn't have this specific permission
      // If permissions are expired, the UserPermissions service will handle the session expired redirect
      const hasValidPermissions = UserPermissions.current.permissions.length > 0;
      if (hasValidPermissions) {
        addErrorBanner(getString("errors.notAuthorized"), false);
        history.push("/");
      }
    }
  }, []);

  const fetchData = async () => {
    await doGetItems({
      searchParams: {
        limit: PAGE_LIMIT,
        offset: 0,
        sortBy: "name",
        sortDirection: "asc",
        filter: JSON.stringify(filters),
      },
    });
  };

  // Load initial values
  useEffect(() => {
    if (error) {
      addErrorBanner(`Error when loading the items: ${error}`);
    }

    if (!items) {
      fetchData();
    }
  }, []);

  // Listen for filters
  useEffect(() => {
    if (items) {
      fetchData();
    }
  }, [filters]);

  // TODO: Implement paging
  const onLoadMore = () => {};

  const shouldLoadMore = () => false;

  if (!items) {
    return (
      <View padding="S700">
        <Spinner size={SpinnerSize.Medium} />
      </View>
    );
  }

  if (paginationInfo?.total === 0) {
    return (
      <PageMainContainer>
        <NoResultsFound searchTerm={"hardware"} />
      </PageMainContainer>
    );
  }

  return (
    <View className="search-hardware-results-container">
      <Row width="100%" height="100%">
        {/* Facets column */}
        <Col width={COLUMN_WIDTH} height="100%" backgroundColor="neutral05">
          {/* Facets */}
          <Col height="100%">
            <HardwareFacetedFilter
              type="category"
              filterName="subCategory"
              title="Category"
              addFacetFilter={addFacetFilter}
            />
            <HardwareFacetedFilter
              type="os"
              filterName="os"
              title="Operating Systems"
              addFacetFilter={addFacetFilter}
            />
          </Col>
          <Col padding={[0, 0, "S400", 0]} height="110px" justifyContent="flex-end">
            <Col width="70%" margin={["S500", 0, 0, "S500"]}>
              <Hr size="wide" color="neutral30" />
            </Col>
            <Text color="neutral70" margin={["S300", 0, 0, "S500"]} fontSize="T14">
              {getString("submitHardwareRequest.header")}
            </Text>
            <Link
              data-testid="request-hardware-link"
              target="_blank"
              textDecoration={LinkTextDecorationOptions.None}
              fontWeight="bold"
              href={getString("submitHardwareRequest.link")}
              color="primary80"
              margin={["S300", 0, 0, 0]}
              padding={[0, 0, 0, "S500"]}
              fontSize="T14"
              data-cy="submit-hardware-request-link"
            >
              {getString("submitHardwareRequest.linkText")}
            </Link>
          </Col>
        </Col>

        {/* Search results */}
        <Col className="search-results-column" padding={"S500"} gridGap={"S500"} ref={ref}>
          {/* Info card */}
          <HardwareUpgradeInfoCard />

          {/* Results */}
          <SearchItemsList
            scrollableTarget="catalog-items-list-container"
            items={items ?? []}
            onSearchNext={onLoadMore}
            shouldLoadMore={shouldLoadMore}
            isLoading={isLoading}
            isLoadingNextItems={false}
            totalCount={paginationInfo?.total ?? 0}
          />
        </Col>
      </Row>
      <Card
        className={`cfm-general ${CFM_STYLE}`}
        data-testid="cfm-card"
        isElevated={true}
        border={"1px solid neutral50"}
        style={{ position: "absolute", top: "55%" }}
      >
        <CustomerFeedback
          moduleName={CFMModuleName.hardwareSearchResults}
          alignment={CustomerFeedbackAlignment.Centered}
          position={CustomerFeedbackIconPosition.Bottom}
          margin={CustomerFeedbackMargins.None}
        />
      </Card>
    </View>
  );
};

export default SearchResultsHardwarePage;
